import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import { isValidAgentModelSlot } from '@/components/Messenger/agent/assistantChatMessageMetadata';

/**
 * Проекция «сырого» списка сообщений из `useChat` + индексов персистенции в ленту для UI.
 *
 * Инварианты:
 * - В `dbBacked`-таймлайн попадают только ассистенты, чей `ui_message_id` уже известен серверу
 *   (иначе дублирование со стриминговым зеркалом и гонки read-receipt).
 * - При активном live-stream зеркале последний ассистент скрывается, пока нет валидного
 *   `model_slot` с сервера — тогда показывается один согласованный пузырь (стрим или БД).
 */

export function isAssistantMessagePersistedForRender(
  message: TrackerChatMessage,
  persistedAssistantIds: Readonly<Record<string, true>>
): boolean {
  if (message.role !== 'assistant') return true;
  if (!persistedAssistantIds[message.id]) return false;
  return isValidAgentModelSlot(message.metadata?.modelSlot);
}

export function filterAgentChatMessagesForDbBackedTimeline(
  messages: readonly TrackerChatMessage[],
  persistedAssistantIds: Readonly<Record<string, true>>
): TrackerChatMessage[] {
  return messages.filter(
    (m) => m.role !== 'assistant' || Boolean(persistedAssistantIds[m.id])
  );
}

/**
 * Стабильность ссылок: если условие «живая трансляция + последний ассистент без полной меты»
 * не срабатывает, возвращается тот же массив `dbBackedMessages` (важно для `memo` списка).
 */
export function projectAgentChatHistoricalFeed(
  dbBackedMessages: TrackerChatMessage[],
  liveStreamActive: boolean,
  persistedAssistantIds: Readonly<Record<string, true>>
): TrackerChatMessage[] {
  if (!liveStreamActive) {
    return dbBackedMessages;
  }
  const last = dbBackedMessages[dbBackedMessages.length - 1];
  if (!last || last.role !== 'assistant') {
    return dbBackedMessages;
  }
  if (isAssistantMessagePersistedForRender(last, persistedAssistantIds)) {
    return dbBackedMessages;
  }
  return dbBackedMessages.length > 0 ? dbBackedMessages.slice(0, -1) : [];
}
